import { StoreStateLens } from 'src/providers/StoreStateProvider';
import { createUseStoreState } from 'src/utils/createUseStoreState';
import { LabelDefinition, LabelType } from 'types/graphql';

export type LabelDef = { id?: number; label: string; color: string; description?: string; labelTypes?: LabelType[] };

export type LabelDefinitionsStore = {
  availableLabels: LabelDef[];
  newLabelDefinitions: LabelDef[];
  labelTypes: LabelType[];
  /** The seed page is not the whole list: the picker says so and searches the server as the user types. */
  moreLabelsOnServer: boolean;
  labelEditing?: { old: LabelDef; new: LabelDef };

  addDefinitions: (labels: LabelDef[]) => void;
  setAvailableLabels: (labels: LabelDef[]) => void;
  upsertAvailableLabels: (labels: LabelDef[]) => void;
  setMoreLabelsOnServer: (more: boolean) => void;
  getLabelColor: (label: string) => string | undefined;
  getAllLabels: () => Set<string>;
  getNewDefinitions: () => { labelTypes: LabelType[]; newLabelDefinitions: LabelDef[] };
  addNewDefinition: (label: LabelDef) => void;
  removeNewLabelDefinition: (label: string) => void;
  clearNewDefinitions: () => void;
  setEditingLabel: (label: LabelDef) => void;
  editLabel: (property: 'labelTypes' | 'color' | 'label', value: any) => void;
  saveEditingLabel: () => boolean;
  deleteEditingLabel: () => boolean;
  cancelEditingLabel: () => void;
};

export const LABEL_DEFINITIONS_SLICE = 'labelDefinitions';

export const createLabelDefinitionsSlice: StoreStateLens<LabelDefinitionsStore, { labelTypes: LabelType[] }> = (
  set,
  get,
  { labelTypes }
) => ({
  availableLabels: [],
  newLabelDefinitions: [],
  labelTypes: labelTypes,
  moreLabelsOnServer: false,
  labelToEdit: undefined,
  addDefinitions: (labels: LabelDefinition[]) => {
    set((state) => {
      state.availableLabels = [...state.availableLabels, ...labels];
    });
  },
  setAvailableLabels: (labels: LabelDef[]) => {
    set((state) => {
      state.availableLabels = labels;
    });
  },
  /**
   * Merges server rows into the known definitions by id: the seed page, the attached labels' definitions and each
   * picker search all land here, so a label found by search can be picked, colored, renamed and deleted.
   */
  upsertAvailableLabels: (labels: LabelDef[]) => {
    set((state) => {
      const indexById = new Map(state.availableLabels.map((label, index) => [label.id, index]));
      for (const label of labels) {
        const index = label.id == null ? undefined : indexById.get(label.id);
        if (index === undefined) {
          indexById.set(label.id, state.availableLabels.length);
          state.availableLabels.push(label);
        } else {
          state.availableLabels[index] = label;
        }
      }
    });
  },
  setMoreLabelsOnServer: (more: boolean) => {
    set((state) => {
      state.moreLabelsOnServer = more;
    });
  },
  addNewDefinition: (label: LabelDefinition) => {
    set((state) => {
      state.newLabelDefinitions = [...state.newLabelDefinitions, label];
    });
  },
  removeNewLabelDefinition: (label: string) => {
    set((state) => {
      state.newLabelDefinitions = state.newLabelDefinitions.filter((l) => l.label !== label);
    });
  },
  getAllLabels: () => {
    const state = get();
    const availableLabels = state.availableLabels.map(({ label }) => label);
    const newLabels = state.newLabelDefinitions.map(({ label }) => label);

    return new Set(availableLabels.concat(newLabels));
  },
  getLabelColor: (label: string) => {
    const labelDef = [...get().availableLabels, ...get().newLabelDefinitions].find((l) => l.label === label);
    return labelDef?.color;
  },
  getNewDefinitions: () => ({ newLabelDefinitions: get().newLabelDefinitions, labelTypes: get().labelTypes }),
  clearNewDefinitions: () => {
    set((state) => {
      state.newLabelDefinitions = [];
    });
  },
  setEditingLabel: (label: LabelDef) => {
    set((state) => {
      state.labelEditing = { old: label, new: label };
    });
  },
  editLabel: (property: 'labelTypes' | 'color' | 'label', value: any) => {
    set((state) => {
      if (state.labelEditing) {
        state.labelEditing.new[property] = value;
      }
    });
  },
  deleteEditingLabel: () => {
    const availableLabelsLength = get().availableLabels.length;

    set((state) => {
      const oldLabel = state.labelEditing.old.label;
      state.availableLabels = state.availableLabels.filter((l) => l.label !== oldLabel);
      state.newLabelDefinitions = state.newLabelDefinitions.filter((l) => l.label !== oldLabel);
    });

    // if available labels length has changed, we know we need to propagate the change to the db
    return availableLabelsLength !== get().availableLabels.length;
  },
  saveEditingLabel: () => {
    const state = get();
    const oldLabel = state.labelEditing.old.label;
    const availableLabelIndex = state.availableLabels.findIndex((l) => l.label === oldLabel);
    const newLabelIndex = state.newLabelDefinitions.findIndex((l) => l.label === oldLabel);

    set((state) => {
      if (!state.labelEditing?.new.label) {
        return;
      }

      if (availableLabelIndex !== -1) {
        state.availableLabels[availableLabelIndex] = state.labelEditing.new;
      }

      if (newLabelIndex !== -1) {
        state.newLabelDefinitions[newLabelIndex] = state.labelEditing.new;
      }

      state.labelEditing = undefined;
    });

    return availableLabelIndex !== -1;
  },
  cancelEditingLabel: () => {
    set((state) => {
      state.labelEditing = undefined;
    });
  },
});

export const useLabelDefinitionsStore = createUseStoreState<LabelDefinitionsStore>(LABEL_DEFINITIONS_SLICE);
